import { ImageName } from "@mcleod/images";
import { CommonDialogs, ImageTable, ModelUsers } from "@mcleod/common";
import {
    BlurEvent, Button, ChangeEvent, ClickEvent, ContextMenuCreationEvent, CrudDecoratorCloseEvent, DataDisplayEvent,
    DataHeaderCloseAction, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, DataSourceValidationEvent, Dialog, EditRowDecorator, Label, Layout, LayoutLoadEvent,
    LookupModelSelectionEvent, ModelDataChangeType, Panel, SaveButton, SlideoutDecorator, Snackbar, TableAction,
    TableContentsChangedEvent, TableRow, TableRowBeforeSaveEvent, Tabset, Textbox, Toast, ValidationResult
} from "@mcleod/components";
import {
    Api, ArrayUtil, CurrencySettings, DisplayType, FieldUpdateEvent, HorizontalAlignment, LogManager,
    McLeodClassicIntegration, Model, ModelRow, ModelRowType, Navigation, StringUtil, SuccessFail, VerticalAlignment
} from "@mcleod/core";
import { ModelEdiorderProfile, OrderLoadTenderHistory } from "@mcleod/datafusion";
import { ModelEdiCustomer } from "@mcleod/datafusion/src/models/ModelEdiCustomer";
import {
    AssetUtil, LMECompanySettings, LMEGeneralSettings, LMEUserSettings, UsersResponsibilitySlideoutTrans
} from "@mcleod/general";
import { CarrierQuickInfo } from "@mcleod/powerbroker";
import { AutogenLayoutBilling } from "./autogen/AutogenLayoutBilling";
import {
    AppointmentUtil, CheckDuplicateBOL, DuplicateBOLSource, OrderHandlingRequirementsSlideout, OrderPostHist,
    OrderPostHistProps, OrderStop, OrderTabView, RearrangeStopSlideout, ShipmentStatusSlideout
} from "@mcleod/dispatch";
import { BrltlUtil } from "@mcleod/dispatch/src/brltl/BrltlUtil";
import { StopProgress } from "@mcleod/dispatch/src/StopProgress";
import { ViewBilling } from "./ViewBilling";
import { BillingOverview } from "./BillingOverview";
import { ManageStops } from "@mcleod/dispatch/src/ManageStops";
import { InvoiceProcessingDeleteDialog } from "./InvoiceProcessingDeleteDialog";
import * as BillingUtil from "./BillingUtil";

const log = LogManager.getLogger("lme.ar.Billing");
const ALL_ITEM = "ALL"
const CREDIT_TYPE = "C";

export { isSpotOrder } from "./BillingUtil";

export class Billing extends AutogenLayoutBilling {
    private _doAfterAction: () => void = () => this.sourceOrders.search({ id: this.mainDataSource.activeRow?.get("order_id") });
    private _invokedFromEdi: boolean = false;
    private _currencyFromProfile: string = null;
    private loadingRecurringOrder: boolean = false;
    private checkDuplicateBOL: CheckDuplicateBOL;
    private _validateRespFilteringDuringAdd = (event: DataSourceValidationEvent) => this.validateRespFilteringDuringAdd(event, this.buttonUsers);
    private _isBrokerageLtlCompany: boolean = false;
    private _isAsset: boolean = false;
    private shouldOpenPaySlideout: boolean;
    private _holdReasonChanged: boolean = false;

    private static orderCurrencies: string[] = ["linehaul_chg", "other_charge", "total_charges",
        "excisetax_total", "totalcharge_and_excisetax", "est_tolls", "pay_gross"];
    public carrierAssignMentChanged: () => void;

    override onLoad(): void {
        this.tabsetUpdateBilling["_header"].borderTopWidth = 0;
        this.checkDuplicateBOL = new CheckDuplicateBOL(this);
        this.addOnSelectListeners();
        this.addBeforeLookupModelListeners();

        this.sourceBilling.addValidationListener(this.validateStopsBeforeSave.bind(this));

        this.stopsTable.onEditLayoutLoaded = this.stopTableOnEditLoaded();

        this.labelHoldBadge.tooltipDynamicContent = () => this.mainDataSource.activeRow?.get("hold_reason");

        this.dataHeader["labelTitle"].minWidth = 220;

        this.layoutRates.checkPickupAndDeliveryEntered = () => {
            const stopData = this.sourceStop.data;
            if (stopData.length < 2) return false;
            const pickupEntered = stopData[0].get("stop_type") === "PU" && stopData[0].get("city_id") != null;
            const deliveryEntered = stopData[stopData.length - 1].get("stop_type") === "SO" && stopData[stopData.length - 1].get("city_id") != null;
            return pickupEntered && deliveryEntered;
        }

        this.setDispatchSettings();
        this.addAdditionalMainDataSourceListeners();
        this.layoutRates.layoutMovementRates.panelOrderCharges.visible = false;
        this.stopsTable.rowsAreDraggable = () => false;
        this.textboxOrderLength.visible = LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y";
        this.layoutShipperPONums.textboxShipperPONums.enabled = false;
    }


    filterAvailableMoves(data: any) {
        return BillingUtil.filterAvailableMoves(data);
    }

    addMovementPickupTimes(tab: Panel, movement: ModelRow<any>) {
        BillingUtil.addMovementPickupTimes(tab, movement);
    }

    addSplitImageToTab(tab: Panel, movement: ModelRow) {
        BillingUtil.addSplitImageToTab(tab, movement);
    }

    async checkForDuplicateBOL(source: DuplicateBOLSource): Promise<SuccessFail> {
        return await this.checkDuplicateBOL.validate(source, this.saveButton);
    }

    textboxBlnumOnBlur(event: BlurEvent) {
        if (!this.mainDataSource.activeRow?.isNull("blnum") && !this.switchRecurringOrder.checked) {
            let source: DuplicateBOLSource;
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                source = DuplicateBOLSource.ADD;
            } else if (this.mainDataSource.mode == DataSourceMode.UPDATE && this.activeRow?.getChangedData()["blnum"] != null) {
                source = DuplicateBOLSource.UPDATE;
            }

            if (source != null) {
                this.checkForDuplicateBOL(source)
                    .then((response) => {
                        if (!response.success)
                            this.textboxBlnum.validationWarning = response.reason;
                    });
            }
        }
    }

    addBeforePostListeners(event: DataSourceExecutionEvent) {
        if (this.mainDataSource.activeRow != null) {
            const row = this.mainDataSource.activeRow;
            row.addBeforePostListener(async postEvent => {
                if (!this.mainDataSource.activeRow?.isNull("blnum")) {
                    await this.checkDuplicateBOL.rowUpdatedBySource(postEvent, this.saveButton);
                }
            });
        }
    }

    doBeforeExecute(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            this.addBeforePostListeners(event);
        }
    }
    async getPendingBadgeTooltipData(modelRow: ModelRow) {
        let tooltipData = null;
        if (modelRow?.isNull("pending_payee_id")) {
            const pendingCarrierType = modelRow.get("pending_carrier_type");
            const pendingCarrierNumber = modelRow.get("pending_carrier_nbr");
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow.get("id"),
                locked_user: modelRow.get("locked_user_id"),
                name: modelRow.get("pending_carrier_name"),
                pending_carrier: true,
                ...(pendingCarrierType == "M" ? { icc_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "D" ? { dot_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "I" ? { intrastate_code: pendingCarrierNumber } : {})
            });
        } else {
            const result = await Api.search("lme/powerbroker/carriers-for-assignment", { id: modelRow?.get("pending_payee_id") });
            const payeeData = result?.data[0];
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow?.get("id"),
                locked_user: modelRow?.get("locked_user_id"),
                name: payeeData.name,
                phone_number: payeeData.phone_number ?? "",
                icc_number: payeeData.icc_number,
                dot_number: payeeData.dot_number,
                intrastate_code: payeeData.intrastate_code,
                pending_carrier: false,
                id: payeeData.id
            });
        }
        return tooltipData;
    }

    async setPendingLabelTooltipContent(pendingLabel: Label, modelRow: ModelRow) {
        pendingLabel.tooltipDynamicContent = async () => {
            const pendingTooltipData = await this.getPendingBadgeTooltipData(modelRow);
            if (pendingTooltipData) {
                return this.getPendingBadgeLayout(pendingTooltipData);
            }
        };
    }

    private getPendingBadgeLayout(modelRow: ModelRow): Layout {
        const layout = Layout.getLayout("lme/powerbroker/CarrierQuickInfo") as CarrierQuickInfo;
        layout.addLayoutLoadListener((event: LayoutLoadEvent) => {
            event.layout.mainDataSource.data = [modelRow];
            layout.mainDataSource.rowIndex = 0;
        });
        layout.pendingCancelled = (lock_changed, user_can_unlock) => {
            if (lock_changed && user_can_unlock) {
                this.sourceOrders.search({ id: this.sourceOrders.activeRow?.get("id") });
            }
        }
        return layout;
    }

    addPendingLabelToTab(tab: Panel, movement: ModelRow) {
        BillingUtil.addPendingLabelToTab(tab, movement, this.setPendingLabelTooltipContent.bind(this));
    }

    createPendingLabel() {
        return BillingUtil.createPendingLabel();
    }

    createMovementTimeLabel(movement: ModelRow<any>) {
        return BillingUtil.createMovementTimeLabel(movement);
    }

    createPostedMovementTimeLabel(movement: ModelRow<any>) {
        return BillingUtil.createPostedMovementTimeLabel(movement);
    }
    updatePnnRateField(movement: ModelRow<any>, restoreExistingRate: boolean) {
        let pnnRate = movement.get("override_targetpay", movement.get("target_pay", null));
        if (restoreExistingRate) {
            if (!StringUtil.isEmptyString(this.sourceOrders.activeRow?.get("pnn_rate"))) {
                pnnRate = this.sourceOrders?.activeRow?.get("pnn_rate");
            }
        }
    }

    textboxPnnLoadboardUserIdOnChange(event) {
        const orderRow = this.sourceOrders.activeRow;
        if (orderRow != null) {
            this.setUpdatedUserPnnCallbackNumber(orderRow.get("pnn_loadboard_user_id"));
        }
    }
    private async setUpdatedUserPnnCallbackNumber(userId: string) {
        if (userId != null) {
            await new ModelUsers().searchSingle({ id: userId }, "common/UserLookup").then(userRow => {
                if (!userRow) return;
                if (userRow.get("phone")) {
                    this.sourceOrders.activeRow.set("pnn_callback", userRow.get("phone"));
                } else {
                    this.sourceOrders.activeRow.set("pnn_callback", "");
                }
            })
        }
    }


    private _setPreferredVendorLookupModelData(row: ModelRow) {
        const lookupData = row?.get("preferred_vendors_lookup_data");
        if (lookupData != null) {
            const newLookupData = lookupData.map(data => {
                const row = new ModelRow("lme/powerbroker/pbw-loadboard-services", false, { ...data });
                row.type = ModelRowType.LOOKUP_MODEL_DATA;
                return row;
            });
            row.setLookupModelData("preferred_vendors", newLookupData);
            row.set("preferred_vendors_lookup_data", undefined);
        }
    }

    private async setCommodity(apiData?: any) {
        const commodityId = this.sourceBilling.activeRow?.get("commodity");
        if (commodityId === null || commodityId === undefined) return;
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });

        if (response != null) {
            const lmModelRow = new ModelRow(this.textboxCommodityId.lookupModel, false, {
                id: response.get("id"),
                descr: apiData?.commodity ?? response.get("descr"),
                temperature_min: apiData?.temperature_min ?? response.get("temperature_min"),
                temperature_max: apiData?.temperature_max ?? response.get("temperature_max"),
                is_hazmat: response.get("is_hazmat"),
                hazmat_code: response.get("hazmat_number"),
                hazmat_number: response.get("hazmat_number"),
                hazmat: response.get("hazmat")
            });

            lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
            this.textboxCommodityId.dataSource.activeRow.setLookupModelData("commodity", lmModelRow);
            this.textboxCommodityId.dataSource.activeRow.set("commodity", response.get("id"));
            this.textboxCommodityId.displayData(this.textboxCommodityId.dataSource.activeRow, null, 0);

            this.textboxHazmatCode.dataSource.activeRow.setLookupModelData("hazmat_code", lmModelRow);
            this.textboxHazmatCode.dataSource.activeRow.set("hazmat_code", response.get("hazmat_number"));
            this.textboxHazmatCode.displayData(this.textboxHazmatCode.dataSource.activeRow, null, 0);

        }

        this.commodityCodeChanged();
    }

    private addAdditionalMainDataSourceListeners() {
        this.mainDataSource.componentValidationFailedCallback = (result: ValidationResult) => this.onComponentValidationFailed(result);
        this.mainDataSource.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceBillingAfterGetDataboundValues(row, dataSource);
    }
    private addOnSelectListeners() {

        this.textboxRevenueCodeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxRevenueCodeId.getFirstLookupModelData()?.get("id") != null)
                    this.revenueCodeChanged(this.textboxRevenueCodeId.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxOrderType.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxOrderType.getFirstLookupModelData()?.get("id") != null)
                    this.orderTypeChanged(this.textboxOrderType.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxCustomerId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxCustomerId.getFirstLookupModelData()?.get("id") != null)
                    (this.layoutOverview as BillingOverview).customerChanged(this.textboxCustomerId.getFirstLookupModelData());

                if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    this.layoutRates.convertPricingCurrencies(event.selection.get("currency_type", await this.getTransactionalCurrency()));
                    if ((this.sourceOrders.mode == DataSourceMode.UPDATE)
                        && !this.sourceOrders.activeRow.isNull("customer_id")) {
                        this.layoutRates.panelExciseTaxTotal.visible = event.selection.getBoolean("excise_taxable", false);
                    }
                }
            }
        });
        this.textboxDefMoveType.addChangeListener((event: ChangeEvent) => {
            this.isAsset = event.newValue === "Asset";
        });
    }

    private addBeforeLookupModelListeners() {
        const userfilter = event => event.filter.web_user_type = "U";
        this.textboxBillingUserId.addBeforeLookupModelSearchListener(userfilter);
        this.textboxEnteredUserId.addBeforeLookupModelSearchListener(userfilter);
    }

    public get isAsset(): boolean {
        return this._isAsset;
    }

    public set isAsset(value: boolean) {
        this._isAsset = value;
        this.textboxBrkQualProfile.visible = !value;
        this.textboxBrokerageWorkflowId.visible = !value;
        this.layoutRates.carrierPayLayout.visible = !AssetUtil.isAssetMoveType(value)
            && (this.activeRow == null || !this.activeRow?.getBoolean("multi_movement", false))
        if (value && this.tableEquipmentRequirementsBrokerage.visible) {
            this.tableEquipmentRequirementsBrokerage.visible = false;
            this.tableEquipmentRequirementsAsset.visible = true;
            this.tableEquipmentRequirementsAsset.dataSource.displayDataInBoundComponents();
        } else if (!value && this.tableEquipmentRequirementsAsset.visible) {
            this.tableEquipmentRequirementsAsset.visible = false;
            this.tableEquipmentRequirementsBrokerage.visible = true;
            this.tableEquipmentRequirementsBrokerage.dataSource.displayDataInBoundComponents();
        }
    }

    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }

    public set isBrokerageLtlCompany(value: boolean) {
        this._isBrokerageLtlCompany = value;
        this.tabBrltlFreightItems.visible = value;
        this.tabAccessorials.visible = value;
        this.layoutRates.panelRatingDetails.visible = value;
        if (value && this.sourceOrders.mode === DataSourceMode.ADD) {
            this.addBlankItemIfBRLTL();
            this.textboxRevenueCodeId.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
            this.textboxOrderType.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
        }

        if (value) {
            this.layoutFreightItems.doBeforeCommoditySearch = (filter: any) => {
                filter.product_book_location = this.sourceStop?.data?.[0].get("location_id");
                filter.product_book_customer = this.activeRow?.get("customer_id");
            };
            this.textboxBolNote.visible = true;
        }
    }

    private onComponentValidationFailed(result: ValidationResult): void {
        const comp = result.component;
        if (comp?.owner instanceof OrderStop)
            comp.owner.onComponentValidationFailed(result);
    }

    dataheaderOnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.activeRow == null || this.sourceBilling.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }
        event.contextMenu.updateMenuLabels({
            sendBackToOperations: {
                visible: true
            },
            cancelZeroDollarBill: {
                visible: this.sourceBilling.data.length > 0 && this.activeRow?.get("total_charges_n") === 0.0
            }
        });
    }

    async textboxCustomerIdOnBlur(event: BlurEvent) {
        if (this.sourceOrders.mode == DataSourceMode.UPDATE && !this.sourceOrders.activeRow.isNull("customer_id")) {
            const ediCustomerRowCount = (await new ModelEdiCustomer().search({ customer_id: this.sourceOrders.activeRow?.get("customer_id") })).actualRowCount;

            this.stopsTable.rows?.forEach(tableRow => {
                const orderStop = tableRow["orderStop"] as OrderStop;
                orderStop.textboxLocationId.googlePlacesProps.customerId = this.sourceOrders.activeRow?.get("customer_id");

                if (this.sourceOrders.activeRow?.get("ordered_method") === "M") {
                    if (ediCustomerRowCount === 0) {
                        orderStop.textboxEdiLocCode.visible = false;
                        orderStop.dfXref.visible = false;
                    }
                    else {
                        orderStop.textboxEdiLocCode.visible = true;
                        if (orderStop.textboxEdiLocCode.text !== "") {
                            orderStop.dfXref.visible = true;
                        }
                    }
                }
            });
        }
    }

    async checkStopsForDistanceChange() {
        const stopdata = this.sourceStop.data;
        let changedText = this.sourceStop.deletedData?.length > 0 ? "removed from" : null;
        for (let i = 0; changedText == null && i < stopdata?.length; i++) {
            if (stopdata[i]._appending) changedText = "added to";
            const changedData = stopdata[i].getChangedData();
            if (changedData?.city_id != null || changedData?.address != null || changedData?.stop_type) changedText = "modified on";
        }
        const locked = this.activeRow?.getBoolean("lock_miles", false)
        if (changedText != null) {
            if (locked) {
                const message = `A stop has been ${changedText} this order which may affect mileage.  Would you like to unlock the mileage?`;
                const unlock = await CommonDialogs.showYesNo(message, "Unlock Miles");
                if (unlock) {
                    this.activeRow.set("lock_miles", "N");
                    this.activeRow.set("bill_distance", null);
                }
            } else {
                this.activeRow?.set("bill_distance", null);
            }
        }
        else {
            this.activeRow?.set("bill_distance", this.activeRow?.originalData["bill_distance"]);
        }
    }

    enableOnHoldComponents(value: boolean) {
        BillingUtil.enableOnHoldComponents(value, this.switchHoldLoad, this.textboxHoldReason);
    }


    private addBlankItemIfBRLTL() {
        if (!this.tabBrltlFreightItems.visible) return;
        const brltlData = BillingUtil.getBRLTLData(this.activeRow);
        if (!BrltlUtil.shouldRateUsingTariff(brltlData)) {
            this.layoutFreightItems.removeBlankFreightItem();
        }
    }

    removeChildDataSources() {
        BillingUtil.removeChildDataSources(this.stopsTable.rows);
    }

    setCompanySettings(mode: DataSourceMode) {
        const companySettings = LMECompanySettings.getSingleton();
        this.textboxDefMoveType.visible = LMECompanySettings.get().show_movement_type;
        this.isAsset = AssetUtil.isAssetMoveType(this.sourceOrders);
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
    }

    setDispatchSettings() {
        const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();

        if (dispatchControlSettings.enforce_billto == 'Y')
            this.textboxCustomerId.required = true;

        if (dispatchControlSettings.enforce_revenu_cod == 'Y')
            this.textboxRevenueCodeId.required = true;
    }

    onHoldLoadChange(event) {
        this.textboxHoldReason.visible = event.target.checked;
        this.textboxHoldReason.required = event.target.checked;
        this.labelHoldBadge.visible = event.target.checked;
        if (!event.target.checked && this.activeRow) {
            this.activeRow.set("hold_reason", null);
        }
    }


    doAfterExecute(event) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            this.activeRow.clearBeforePostListeners();
            this.sourceOrders.addAfterSearchPlusChildrenListener(async () => {
                if (this.shouldOpenPaySlideout) {
                    history.replaceState({ additionalProps: { modifyPay: true } }, "", window.location.search);
                    this.panelMovementTab.clicked();
                    this.shouldOpenPaySlideout = false;
                }

                // These fields are really part of the movement table, but they are used in the order's API data to update the movement.
                // They are no longer needed on the active row after posting, so set them to undefined to prevent reenabling the save button
                if (!this.sourceOrders.activeRow?.isNull("override_targetpay")) {
                    this.sourceOrders.activeRow.set("override_targetpay", undefined);
                }
                if (!this.sourceOrders.activeRow?.isNull("override_max_pay")) {
                    this.sourceOrders.activeRow.set("override_max_pay", undefined);
                }
            });
        } else if (event.getAction() === DataSourceAction.SEARCH) {
            this.addLayoutDataLoadListener(async () => {
                const overviewLayout = this.layoutOverview as BillingOverview;
                (overviewLayout.layoutStopProgress as StopProgress).setStopsTable(this.stopsTable);
                const isYSplit: boolean = this.stopsTable.data.some(row => row.get("stop_type") === "SP" || row.get("stop_type") === "SD");
                await overviewLayout.showErrorPanel(isYSplit);
                const orderId = this.sourceOrders.activeRow?.get("id");
                if(orderId) {
                    (this.layoutImageTable as ImageTable).populateForRow("O", orderId);
                }
                this.switchRecurringOrder.checked = (null != this.sourceOrders.activeRow?.get("recurring_order_id"));
                this.layoutRates.carrierPayLayout.configureForOrderEntry(this.sourceOrders, true);
                const isHazmat = await this.getIsHazmatCommodity();
                this.showHazmatWarning(isHazmat);
                this.switchTenderReplyTransmitted.visible = this.activeRow?.getBoolean("reply_transmitted", false);
                if (this.isBrokerageLtlCompany)
                    this.getRatingDetails(this.sourceOrders.activeRow?.get("id"));
                this.setupPreloadedTrailerLookup();
            });
        }
        const multiMoveOrder = this.activeRow?.getBoolean("multi_movement", false);
        this.layoutRates.layoutMovementRates.visible = !this.isAsset && !multiMoveOrder;
        this.layoutRates.panelOrderCarrierPay.visible = !this.isAsset && multiMoveOrder;
        this.setupDefMoveType();
    }

    setupDefMoveType() {
        const assetAndBrokerage = this.activeRow?.get("def_move_type") == "L";
        if (assetAndBrokerage) {
            this.textboxDefMoveType.text = "Asset & Brokerage";
        }
    }

    async setPostingOverrideFields() {
        const orderRow = this.sourceOrders?.activeRow;
        const overrideArriveLate = orderRow.get("override_arrive_late");
        const overrideArriveEarly = orderRow.get("override_arrive_early");
        if (overrideArriveEarly === undefined && overrideArriveLate === undefined) {
            orderRow.set("override_arrive_early", new Date(orderRow.get("stop_origin.sched_arrive_early")));
            if (orderRow.get("stop_origin.sched_arrive_late") !== undefined) {
                orderRow.set("override_arrive_late", new Date(orderRow.get("stop_origin.sched_arrive_late")));
            }
        }

    }


    getRatingDetails(orderId: string) {
        this.layoutRates.sourceRatingDetails.search({ "order_id": orderId }).then(response => {
            this.layoutRates.sourceRatingDetailsSummary.search({ "order_id": orderId }).then(response => {
                this.layoutRates.switchByFreightItem.checked = true;
                this.layoutRates.tableRevSummary.visible = false;
            });
        });
    }

    async doAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
            this.stopsTable.dataSource = null;
            this.textboxOrderedDate.displayType = DisplayType.DATERANGE;
        } else if (this.stopsTable.dataSource == null)
            this.stopsTable.dataSource = this.sourceStop;

        this.makeSubjectOrderAdjustments();
        if (event.newMode === DataSourceMode.UPDATE) {
            const billType = this.mainDataSource.activeRow?.get("bill_type");
            if (billType === CREDIT_TYPE) {
                this.disableCreditFields(this.tabsetUpdateBilling);
                // Enable printed checkbox for credits when rendition billing is enabled
                const arControl = LMEGeneralSettings.getSingleton().getARControl();
                if (arControl?.rendition_billing === "Y") {
                    // Enable parent chain since disableCreditFields disabled them
                    this.tabInvoiceProcessing.enabled = true;
                    this.panel6.enabled = true;
                    this.checkboxPrinted.enabledDuringUpdate = true;
                    this.checkboxPrinted.enabled = true;
                }
            }
            if (this.sourceOrders.activeRow?.get("credit_override_status") == "P" && this.sourceOrders.activeRow?.get("on_hold", null)) {
                this.enableOnHoldComponents(false);
            }
        }
        this.activeRow?.addAfterFieldUpdateListener((event) => {
            if (event.fieldName === "commodity" && this.textboxCommodityId.dataSource.actionInProgress == null) {
                this.commodityCodeChanged();
            } else if (event.fieldName === "distance") {
                this.updateLabelBillingDistance();
            }
        })
        this.tabImages.visible = event.newMode !== DataSourceMode.SEARCH;
        this.setCompanySettings(event.newMode);
    }

    private updateLabelBillingDistance() {
        BillingUtil.updateLabelBillingDistance(this.layoutOverview, this.layoutRates);
    }

    makeSubjectOrderAdjustments() {
        BillingUtil.makeSubjectOrderAdjustments(this.activeRow, this.textboxCustomerId);
    }

    disableCreditFields(component?: Layout | TableRow | Tabset) {
        const ignoreList = ['tableImages', 'buttonMoveDetails', 'tabsetAddOrders', 'tabsetStopNotesRefNos'];
        const rootComponent = component ?? this;
        BillingUtil.disableCreditFields(rootComponent, ignoreList);
    }

    doBeforeModeChange(event: DataSourceModeChangeEvent) {
        this.hideSuffix = event.newMode != DataSourceMode.ADD;

        this.dataheaderOrders.addlPanelLeft.visible =
            event.newMode === DataSourceMode.ADD ||
            event.newMode === DataSourceMode.UPDATE ||
            event.newMode === DataSourceMode.SEARCH;
        if (event.newMode === DataSourceMode.SEARCH) {
            this.dataheaderOrders.addlPanelLeft.components.forEach(component => {
                if (component !== this.buttonUsers)
                    component.visible = false;
            });
        }

        this.panelDataHeaderUpdateOnly.visible = event.newMode === DataSourceMode.UPDATE;
        if (event.newMode === DataSourceMode.UPDATE) {
            const titleNumber = !StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("invoice_no_string"))
                ? this.mainDataSource.activeRow?.get("invoice_no_string")
                : this.mainDataSource.activeRow?.get("order_id");
            this.title = "Invoice " + titleNumber;
            this._setPreferredVendorLookupModelData(this.sourceOrders.activeRow);
        }

        this.tabOverview.visible = event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.NONE;
        this.tabInvoiceProcessing.visible = event.newMode !== DataSourceMode.ADD;
        this.tabDataFusion.visible = event.newMode !== DataSourceMode.ADD;
        this.tabStops.visible = event.newMode !== DataSourceMode.SEARCH;
    }

    commodityCodeChanged() {
        if (this.activeRow != null) {
            const commodityCleared = this.activeRow.isNull("commodity");
            const lookupData = commodityCleared ? null : this.textboxCommodityId.getFirstLookupModelData();
            const hazmatModelRow = commodityCleared ? null : new ModelRow(this.textboxHazmatCode.lookupModel, false, { id: lookupData?.get("hazmat_number") });

            this.showHazmatWarning(lookupData?.get("hazmat_number") != null);
            this.sourceOrders.activeRow.setLookupModelData("hazmat_code", hazmatModelRow);
            this.sourceOrders.activeRow.set("hazmat_code", lookupData?.get("hazmat_number"));
            this.activeRow.set("commodity_descr", lookupData?.get("descr"));
            this.sourceOrders.activeRow.set("hazmat", lookupData?.get("hazmat", "N"));
            this.sourceOrders.activeRow.set("temperature_min", lookupData?.get("temperature_min"));
            this.sourceOrders.activeRow.set("temperature_max", lookupData?.get("temperature_max"));
            this.sourceOrders.activeRow.set("placard_required", lookupData?.get("placard_required", null));
        }
    }

    getBRLTLData() {
        return BillingUtil.getBRLTLData(this.activeRow);
    }

    showHazmatWarning(hazmat: boolean) {
        BillingUtil.showHazmatWarning(this.panelCommodityHazmat, this.labelHazmatCommodity, hazmat);
    }

    buttonAddAnotherStopOnDataDisplay(event: DataDisplayEvent) {
        BillingUtil.buttonAddAnotherStopOnDataDisplay(event);
    }

    //** This is an event handler for stopsTable onRowBeforeSave */
    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data;
        const orderStopLayout: OrderStop = event.target["orderStop"] as OrderStop;
        modelRow.set("stop_notes", orderStopLayout.commentsAndRefNbrs.sourceStopNote?.data || []);
        modelRow.set("stop_reference_numbers", orderStopLayout.commentsAndRefNbrs.sourceReferenceNumber?.data || []);
        const changedData = modelRow.getChangedData();
        if (AppointmentUtil.apptConfirmCheck(changedData, modelRow))
            orderStopLayout.getAppointmentChangedRow();
        else {
            modelRow.set("appointment_change", null);
        }
    }

    /** This is an event handler for the onRowDisplay event of stopsTable.  */
    stopsTableOnRowDisplay(event) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.allowDelete = tableRow.index != 0;
        const labelSequence = (tableRow.findComponentById("labelSequence")) as Label;
        labelSequence.text = (tableRow.index + 1).toString();
        const buttonManageStops = (tableRow.findComponentById("buttonManageStops")) as Panel;
        buttonManageStops.visible = this.sourceStop.mode != DataSourceMode.ADD;

        const layoutOrderStop = tableRow.findComponentById("layoutOrderStop") as OrderStop;
        const billType = this.mainDataSource.activeRow?.get("bill_type");
        if (billType === CREDIT_TYPE) {
            this.disableCreditFields(tableRow);
        }
        this.sourceStop.preventChangeNotifications = true;
        tableRow["orderStop"] = layoutOrderStop;
        layoutOrderStop.calculateRates = () => this.checkStopsForDistanceChange().then(() => {
            this.layoutRates.calculateRates(false, false)
        });
        layoutOrderStop.commodityChanged = () => {
            this.setCommodity();
        }
        layoutOrderStop.initializeBilling(false, tableRow, this.sourceOrders, this.sourceStop, this.sourceBilling.mode, this.sourceBilling);
        layoutOrderStop.loadingRecurringOrder = this.loadingRecurringOrder;
        layoutOrderStop.invokedFromEDI = this.getInvokedFromEdi();
        if (this.getInvokedFromEdi()) {
            layoutOrderStop.setStopDateTypeForEdi();
        }
        layoutOrderStop.setFreeFormVisibility();
        if (this.getInvokedFromEdi() || this.loadingRecurringOrder || (this["quote_id"] && !this.hasPortalContactInfo(tableRow.data))) {
            layoutOrderStop.doAfterLocationIdChanged();
        }
        if (tableRow.index == 0) {
            if (this.isBrokerageLtlCompany) {
                this.layoutShipperPONums.initialize({
                    "stopSource": this.sourceStop,
                    "firstStopRow": tableRow.data
                });
            }
            layoutOrderStop.switchPalletsRequired.displayData(this.sourceOrders.activeRow, null, 0);
            layoutOrderStop.textboxPalletsHowMany.displayData(this.sourceOrders.activeRow, null, 0);
        }
        if (layoutOrderStop.commentsAndRefNbrs.enabled == false) {
            const commentsTable = layoutOrderStop.commentsAndRefNbrs.tableStopComments;
            commentsTable.allowEdit = false;
            const tableRefNbrs = layoutOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers;
            tableRefNbrs.allowEdit = false;
            layoutOrderStop.commentsAndRefNbrs.tabsetStopNotesRefNos.expandAll();
        }
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            tableRow.data.addAfterFieldUpdateListener(async (event: FieldUpdateEvent) => {
                if (event.fieldName === "sched_arrive_early" && (this.sourceOrders.mode == DataSourceMode.ADD
                    || "A" == this.sourceOrders.activeRow?.getString("status")) && !this.sourceOrders.actionInProgress) {
                    await this.setConversionDates(this.getConversionDateFromStops());
                }
            });
        }
        this.sourceStop.preventChangeNotifications = false;
        layoutOrderStop.checkLocationIsTrailerPool = (usePool: boolean) => { this.setPreloadedTrailerLookupInfo(usePool) };
        this.enableCaptions(layoutOrderStop);
    }

    async validateStops(event: TableContentsChangedEvent) {
        const stopData = this.sourceStop.data;
        let errorMessage = null;

        if (stopData.length < 2) {
            errorMessage = "An order must have at least two stops.";
        } else if (!stopData.some(stop => stop.get("stop_type") === "PU")) {
            errorMessage = "Cannot delete the only pickup stop.";
        } else if (!stopData.some(stop => stop.get("stop_type") === "SO")) {
            errorMessage = "Cannot delete the only delivery stop.";
        } else if (stopData[stopData.length - 1].get("stop_type") !== "SO") {
            errorMessage = "The delivery stop should be the last stop.";
        }

        if (errorMessage) {
            await CommonDialogs.showError(errorMessage, "Cannot Delete Stop");
            event.preventDefault();
            const orderId = this.sourceOrders.activeRow?.get("id");
            if (orderId) {
            await this.sourceBilling.search({ "orders.id": orderId });
            }
        }
    }

    validateStopsBeforeSave(event: DataSourceValidationEvent): void {
        if (event.dataSource.mode !== DataSourceMode.ADD && event.dataSource.mode !== DataSourceMode.UPDATE) {
            return;
        }
        const stopData = this.sourceStop.data;
        BillingUtil.validateStopsBeforeSave(event, stopData, this.stopsTable);
    }

    private enableCaptions(comp: any) {
        BillingUtil.enableCaptions(comp);
    }

    private revenueCodeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": newValue,
                "order_type_id": this.mainDataSource.activeRow?.get("order_type_id"),
                "current_mode": this.sourceOrders.activeRow?.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.sourceOrders.activeRow.set("order_mode", newMode);
            });
    }

    private hasPortalContactInfo(data: ModelRow) {
        return BillingUtil.hasPortalContactInfo(data);
    }

    private orderTypeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": this.mainDataSource.activeRow?.get("revenue_id"),
                "order_type_id": newValue,
                "current_mode": this.sourceOrders.activeRow?.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.sourceOrders.activeRow.set("order_mode", newMode);
            });
    }

    displayBillingUsersSlideout() {
        const urs = new UsersResponsibilitySlideoutTrans("lme/ar/BillingUsers", this.getBillingUsersSlideoutTitle(), this.getBillingUsersIdFilter(),
            this.getBillingUsersSharedFieldNames(), this.sourceBilling, this.sourceResponsibleHist, this.mainDataSource.activeRow?.get("bill_type") === CREDIT_TYPE);
        urs.show(this.mainDataSource.activeRow?.get("bill_type") !== CREDIT_TYPE);
    }

    private getBillingUsersSlideoutTitle(): string {
        const name = this.sourceBilling.activeRow?.get("order_id");
        return "Billing Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getBillingUsersIdFilter(): any {
        const id = this.sourceBilling.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getBillingUsersSharedFieldNames(): string[] {
        return ["operations_user", "agent", "entered_user_id", "billing_user_id"];
    }

    sourceBillingAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set these fields when in search mode because they aren't bound fields for the main layout
            for (const field of this.getBillingUsersSharedFieldNames()) {
                row.set(field, dataSource.searchRow.get(field));
            }

            //have to add hierarchy search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceResponsibleHist.data))
                return;
            row.addLinkedModel({ model: this.sourceResponsibleHist.url, rows: [this.sourceResponsibleHist.data[0]] });
        }
    }

    textboxPnnLoadboardUserIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    public static navigateTo(id: string, newTab = true): void {
        ViewBilling.navigateTo({ key: id, newTab: newTab });
    }


    stopTableOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const thisOrderStop = tableRow["orderStop"] as OrderStop;
            const editOrderStop = rowDecorator.layout as OrderStop;
            if (this.sourceOrders.isAddingOrUpdating()) {
                editOrderStop.commentsAndRefNbrs.tableStopComments.dataSource.mode = DataSourceMode.UPDATE;
                editOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers.dataSource.mode = DataSourceMode.UPDATE;
            }
            editOrderStop.mainDataSource.activeRow?.set("stop_notes", thisOrderStop?.commentsAndRefNbrs.tableStopComments.data);
            editOrderStop.mainDataSource.activeRow?.set("reference_numbers", thisOrderStop?.commentsAndRefNbrs.tableStopReferenceNumbers.data);
            editOrderStop.initialize(true, tableRow, this.sourceOrders, this.sourceStop);
            editOrderStop.slideOutTableRow = tableRow;

            rowDecorator.onSave = this.stopSlideoutOnSave(rowDecorator, tableRow);
            rowDecorator.btnDelete.visible = false;
            rowDecorator.layout.width = window.innerWidth * .75;
            rowDecorator.labelTitle.text = "Order Entry Stop Details - " + this.title;
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    stopSlideoutOnSave(rowDecorator: EditRowDecorator, tableRow: TableRow): (updatedData: ModelRow | any) => void {
        const orderStop = (rowDecorator.layout as OrderStop);
        const thisOrderStop = tableRow["orderStop"] as OrderStop;
        return async (_updatedData: ModelRow | any) => {
            tableRow.saveChangesFromEdit(tableRow.data);
            thisOrderStop.textboxLocationId["_lookupModelDataForUpdate"] = orderStop.textboxLocationId["_lookupModelDataForUpdate"]; // syncs lookupModelDataForUpdate which is used in updateBoundData in Location.ts
            if (tableRow.index == 0) {
                this.sourceOrders.activeRow.set("pallets_how_many", orderStop.textboxPalletsHowMany.text);
                this.sourceOrders.activeRow.set("pallets_required", true === orderStop.switchPalletsRequired.checked ? "Y" : "N");
            }
            orderStop.commentsAndRefNbrs.saveChangesFromEdit(tableRow["orderStop"].commentsAndRefNbrs);
            thisOrderStop.textboxLocationId.displayData(tableRow.data, null, 0);
            thisOrderStop.setLocationTextAfterLocation(thisOrderStop.textboxLocationId.textCombined, tableRow.data, "location_id");
            thisOrderStop.calculateRates();
            thisOrderStop.activeRow.set("initial_appointment_change", null); // reset in scenario if user exits slideout and makes another update on main screen
        };
    }

    viewLoadTenders() {
        const id = this.sourceOrders.activeRow?.data?.["id"];
        log.debug("id", id);
        if (id) {
            const layout = Layout.getLayout("lme/datafusion/OrderLoadTenderHistory", {
                maxWidth: 900,
                width: window.innerWidth * .60,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as OrderLoadTenderHistory;

            new SlideoutDecorator({
                layout: layout,
                title: "Tenders - Order " + id,
                fillVerticalSpace: true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ order_id: id })
            });

            layout.orderId = id;
        }
    }

    viewShipmentStatus() {
        const id = this.sourceOrders.activeRow?.data?.["id"];
        const companyId = this.sourceOrders.activeRow?.data?.["company_id"];
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                //maxWidth: 900,
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;

            new SlideoutDecorator({
                layout,
                title: `Order Number ${id}`,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                onClose: () => {
                    this.sourceOrders.search({ id: id });
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.sourceEdistatus.search({ order_id: id });
                    layout.sourceEdiOrder.search({ order_id: id });
                    layout.sourceEdiMovementOrder.search({ order_id: id, company_id: companyId });
                    layout.saveButton.addClickListener(async event => {
                        if (decorator.validateSimple()) {
                            await layout.saveChanges();
                        }
                    });
                },
                doAfterSlideOut: (decorator: SlideoutDecorator) => {
                    this.sourceOrders.search({ id: id });
                }
            });
        }
    }

    setupPreloadedTrailerLookup() {
        const data = this.sourceStop?.data[0]?.get("location");
        if (data != null && data.length > 0) {
            const isTrailerPool = data[0].is_trailer_pool;
            const usePool = isTrailerPool == "Y" ? true : false;
            this.setPreloadedTrailerLookupInfo(usePool);
        }
    }

    setPreloadedTrailerLookupInfo(usePool: boolean) {
        if (usePool) {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/trailer-pool";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerPoolLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelResultField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = true;
            this.textboxPreloadTrailerId.placeholder = "Trailer Pool";
        } else {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/asset/trailer-lookup";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "id";
            this.textboxPreloadTrailerId.lookupModelResultField = "id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = false;
            this.textboxPreloadTrailerId.placeholder = "All Trailers";
        }
    }

    createPostToLoadBoardDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Orders on hold may not be posted to the load boards.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const lblCaptionLine2 = new Label({
            text: "Remove the hold to post this order.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        })
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        })
        pnlCaption.add(lblCaptionLine1, lblCaptionLine2);

        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();

    }

    createUpdateDeniedDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Order has been billed and you do not have permission to update an order " +
                "after the order has been billed",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        });
        pnlCaption.add(lblCaptionLine1);
        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.ar.EntryBilling", "<ext> search id ='" + this.mainDataSource.activeRow?.get("id") + "'");
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    buttonOrderHistoryOnClick(event: ClickEvent) {
        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: this.activeRow?.get("order_id"),
            movementId: this.sourceOrders.activeRow?.get("curr_movement_id"),
            isBilled: !this.activeRow.isNull("bill_date") || !this.activeRow.isNull("transfer_date"),
        }
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
    }

    equipItemTableOnRowDisplay(event) {
        const row: TableRow = event.getTableRow();
        let equipType = (row.findComponentById("textboxMatchEquipmentTypeId")) as Textbox;
        if (equipType == null) {
            equipType = (row.findComponentById("textboxMatchEquipmentTypeIdAsset")) as Textbox;
        }
        if (equipType != null && row.data.get("equipment_match_id") != undefined) {
            equipType.tooltipDynamicContent = () => {
                const panel = new Panel({ padding: 0, margin: 0 });
                panel.add(new Label({ text: "Match ID", fontSize: "medium", fontBold: true }));
                panel.add(new Label({ text: row.data.get("equipment_match_id") }));
                return panel;
            }
        }

        if (this.tableEquipmentRequirementsAsset.visible && this.tableEquipmentRequirementsAsset.quickAddRow === row && row != null) {
            this.updateStopNumberDropdown();
        }
    }

    updateStopNumberDropdown() {
        const textboxStopNumber = this.tableEquipmentRequirementsAsset?.quickAddRow?.findComponentById("textboxStopNumber") as Textbox;
        if (textboxStopNumber) {
            const items = [];
            items.push(ALL_ITEM);
            Array.from({ length: this.stopsTable.rowCount }, (_, i) => i + 1).forEach(stopNumber => items.push(stopNumber));
            textboxStopNumber.items = items;
        }
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }

    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }
    public get holdReasonChanged(): boolean {
        return this._holdReasonChanged;
    }
    public set holdReasonChanged(value: boolean) {
        this._holdReasonChanged = value;
    }
    async stopsTableOnContentsChanged(event: TableContentsChangedEvent) {
        if (TableAction.DELETE == event.getAction()) {
            await this.validateStops(event);
            if (event.defaultPrevented) {
            this.checkStopsForDistanceChange().then(() => {
                this.layoutRates.calculateRates(false, false);
            });
        }
        }
        this.updateStopNumberDropdown();
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.sourceOrders.mode !== DataSourceMode.ADD && "A" == this.sourceOrders.activeRow?.getString("status")) {
                await this.setConversionDates(this.getConversionDateFromStops());
            }
        }
    }

    private validateRespFilteringDuringAdd(event: DataSourceValidationEvent, buttonUsers: Button) {
        if (event.dataSource.activeRow?.get("rf_entry_code_required", false) !== true)
            return;
        let rfDataSource: DataSource = null;
        if (event.childDataSources != null) {
            for (const childDataSource of event.childDataSources) {
                if (childDataSource.url === "lme/general/responsible-hist") {
                    rfDataSource = childDataSource;
                    break;
                }
            }
        }
        if (rfDataSource != null) {
            for (const row of rfDataSource.data) {
                if (row.get("responsible_role", null) === "E" &&
                    (row.get("hierarchy_level_1", null) != null || row.get("hierarchy_level_2", null) != null || row.get("hierarchy_level_3", null) != null ||
                        row.get("hierarchy_level_4", null) != null || row.get("hierarchy_level_5", null) != null)) {
                    //an entry row is present, return without adding anything to the validation results
                    return;
                }
            }
        }
        const message = "A responsibility record with role 'Entry' must be specified";
        event.validationResults.push({
            isValid: false,
            validationMessage: message,
            caption: message,
            component: buttonUsers
        });
    }

    private get saveButton(): SaveButton {
        return this.dataHeader?.["saveButton"];
    }

    public displayOrderCreatedToast(orderId?: string) {
        const id = orderId ?? this.sourceOrders?.activeRow?.get("id", null);
        const toast = Toast.showToast(this.getOrderCreatedToast(id, "created", () => this.orderToastLinkOnClick(id, toast)), null);
    }

    private orderToastLinkOnClick(key: string, toast: Toast) {
        ViewBilling.navigateTo({ key, newTab: false });
        toast.dismiss();
    }

    private getOrderCreatedToast(orderId: string, action: string, doOnLinkClick: () => void): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0
        };
        let labels: Label[] = [];
        if (!StringUtil.isEmptyString(orderId)) {
            labels = [
                new Label({ ...labelProps, text: "Order " }),
                new Label({
                    ...labelProps,
                    style: { textDecoration: "underline" },
                    text: orderId,
                    onClick: event => doOnLinkClick()
                }),
                new Label({ ...labelProps, text: " " + action + "." })
            ];
        } else {
            labels = [
                new Label({ ...labelProps, text: "Order not " + action + "." })
            ];
        }
        return new Panel({ align: HorizontalAlignment.CENTER, fillRow: true, components: labels });
    }

    imageAddHandlingReqOnClick() {
        OrderHandlingRequirementsSlideout.showSlideout({
            hdrDataSource: this.sourceBrltlOrderHdrXFgp,
            hdrChanged: (row: ModelRow, type: ModelDataChangeType) => this.hdrChanged(row, type),
            onSave: (hasChanged: boolean) => {
                this.layoutHandlingReqs.populateHdrs(this.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                    this.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                });
            },
            invokedFromEdi: false
        })
    }

    hdrChanged(hdr: ModelRow, type: ModelDataChangeType) {
        if (type == ModelDataChangeType.ADD) {
            hdr.set("fgp_uid", this.sourceOrders.activeRow?.get("fgp_uid"));
            hdr.set("order_id", this.sourceOrders.activeRow?.get("id"));
            hdr._appending = true;
            this.sourceBrltlOrderHdrXFgp.addRow(hdr, this.sourceBrltlOrderHdrXFgp.data.length, false);
        } else if (type == ModelDataChangeType.DELETE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach(row => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    this.sourceBrltlOrderHdrXFgp._deleteTableRow(row, true);
                }
            });
        } else if (type == ModelDataChangeType.UPDATE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach((row, index) => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    row.setValues({
                        "text_value": hdr.data["text_value"],
                        "integer_value": hdr.data["integer_value"],
                        "float_value": hdr.data["float_value"],
                        "time_value": hdr.data["time_value"],
                        "date_value": hdr.data["date_value"]
                    });
                }
            });
        }

    }

    enableStatusButton() {
        const orderStatus = this.sourceOrders.activeRow?.get("status");
        this.buttonInitiateStatusProcessing.enabled = (orderStatus != "Q" && orderStatus != "V" && orderStatus != "S");
    }

    async initiateStatusProcessing(event: ClickEvent): Promise<any> {
        const orderId = this.sourceOrders.activeRow?.get("id");
        if (orderId == null) return;
        this.buttonInitiateStatusProcessing.busy = true;
        const mode = this.sourceOrders.mode;
        this.sourceOrders.activeRow?.set("shipstatus2edi_dt", null);
        this.sourceOrders.displayDataInBoundComponent(this.textboxOutboundStatusCompleted);

        await this.validateProcessStatus(orderId, mode).then(async response => {
            await this.delay(3000).then(() => {
                this.sourceOrders.search({ id: orderId }).then((order) => {
                    const row = order.modelRows[0].data;
                    const statusCompletedDate = row["shipstatus2edi_dt"];
                    this.textboxOutboundStatusCompleted.text = statusCompletedDate;
                    this.sourceOrders.displayDataInBoundComponent(this.textboxOutboundStatusCompleted);
                });
            });
        });
        this.buttonInitiateStatusProcessing.busy = false;
    }

    async validateProcessStatus(orderId: string, mode: string): Promise<any> {
        return await Api.post("lme/datafusion/status-processing", { order_id: orderId, datasource_mode: mode });
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    sourceBrltlOrderHdrXFgpAfterExecution(event: DataSourceExecutionEvent) {
        this.layoutHandlingReqs.populateHdrs(event.dataSource.data, true);
    }

    getStopDataSource(): DataSource {
        return this.sourceStop;
    }

    getHdrXFgpDataSource(): DataSource {
        return this.sourceBrltlOrderHdrXFgp;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxOrderType. */
    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    displaySuccessfulCancelToastMessage(orderId: string, movementId: string) {
        const lbl = new Label({ text: `Success!\n\rThe load posting for Order ${orderId} has been canceled`, align: HorizontalAlignment.CENTER, fontBold: true, fontSize: "xxlarge" });
        Toast.showToast(lbl, { backgroundColor: "primary" });
    }

    displayFailedCancelToastMessage(orderId: string, successfulCancellationsVendors: string, failedCancellationsVendors: string) {
        if (!StringUtil.isEmptyString(successfulCancellationsVendors) && !StringUtil.isEmptyString(failedCancellationsVendors)) {
            const partialSuccessful = `Success! The load posting for ${orderId} has been canceled for: ${successfulCancellationsVendors}`;
            Toast.showToast(partialSuccessful, null, { persist: true });

            const partialFailure = `The load posting for Order ${orderId} was not canceled for: ${failedCancellationsVendors}. Please retry directly on the load board site.`
            Snackbar.showWarningSnackbar(partialFailure);
        }
        else if (!StringUtil.isEmptyString(successfulCancellationsVendors)) {
            const loadBoardCancellationSuccessful = `${successfulCancellationsVendors} cancellation of load posting was successful.`;
            Toast.showToast(loadBoardCancellationSuccessful, null, { persist: true });
        } else if (!StringUtil.isEmptyString(failedCancellationsVendors)) {
            const loadBoardCancellationFailed = `${failedCancellationsVendors} cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        } else {
            // This condition occurs when there is not a matching load_board_trx record for the order_id and movement_id.
            const loadBoardCancellationFailed = `Cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        }
    }


    public set currencyFromProfile(value: string) {
        this._currencyFromProfile = value;
    }

    private getConversionDateFromStops(): Date {
        const conversionBy: string = CurrencySettings.getConversionBy();
        const numberOfStops = this.stopsTable.rows.length;
        if (numberOfStops > 0) {
            const stop: TableRow = "S" == conversionBy ? this.stopsTable.rows[0] : this.stopsTable.rows[numberOfStops - 1];
            return stop.data.get("actual_arrival") ?? stop.data.get("sched_arrive_early") ?? new Date();
        }
        return new Date();
    }

    private async setConversionDates(conversionDate: Date) {
        const dataRow: ModelRow<any> = this.sourceOrders.activeRow ?? this.sourceOrders.data[0];
        if (conversionDate) {
            Billing.orderCurrencies.forEach(fieldName => {
                const currency = dataRow.getCurrency(fieldName);
                if (currency && conversionDate != currency.getConversionDate()) {
                    currency.setConversionDate(conversionDate);
                    dataRow.set(fieldName, currency);
                }
            });
            this.layoutOverview.setCarrierRateData();
            await this.layoutRates.calculateRates(false, false);
            this.layoutRates.layoutMovementRates.validateCarrierPayOverrideDates();
        }
    }

    public getCurrencyFromProfile(): string {
        return this._currencyFromProfile;

    }

    async getTransactionalCurrency(): Promise<string> {
        let transactionCurrency = CurrencySettings.getTransactionalCurrency();
        const partner = this.sourceOrders.activeRow?.getString("partner_id");
        const version = this.sourceOrders.activeRow?.getString("partner_version");
        if (this.getInvokedFromEdi() && this.getCurrencyFromProfile()) {
            transactionCurrency = this.getCurrencyFromProfile();
        } else if (partner && version) {
            const profile = await new ModelEdiorderProfile().searchSingle({
                partner_id: partner,
                version: version
            });
            if (profile.data.currency_type_id) {
                transactionCurrency = profile.data.currency_type_id;
            }
        }
        return transactionCurrency;
    }

    textboxCommodityIdOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set("commodity_descr", null);
            this.textboxCommodity.text = null;
        }
    }

    textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const descr = event.selection.get("descr");
            this.activeRow.set("commodity_descr", descr);
            this.textboxCommodity.text = descr;
        }
    }

    async getIsHazmatCommodity(): Promise<boolean> {
        const commodityId = this.sourceBilling.activeRow?.get("commodity");
        if (commodityId === null || commodityId === undefined) return;
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });
        if (response != null) {
            return response?.get("is_hazmat") === "Y";
        } else {
            return false;
        }
    }

    // Used in onClickListener for Manage Stops button
    private _manageStops() {
        if (this.sourceStop.hasChanged()) {
            Snackbar.showWarningSnackbar("Save previous stop edits before opening Manage Stops.");
            return;
        }
        const slideout = new RearrangeStopSlideout("O", this.mainDataSource?.activeRow?.get("order_id"));
        slideout.showSlideout(
            (manageLayout: ManageStops) => { },
            (canceled: boolean) => {
                if (!canceled) {
                    Toast.showSuccessToast("Changes were saved successfully.");
                    ViewBilling.navigateTo({ key: this.activeRow?.get("id"), newTab: false });
                }

            }
        );
    }

    textboxOnHoldReasonCodeBeforeLookupModelSearch(event) {
        event.filter.code_type = "%B%";
        event.filter.is_active = "Y";
    }

    textboxOnHoldReasonCodeAfterLookup(event) {
        if (event?.selections?.length > 0) {
            this.textboxHoldReasonText.enabled = true;
        } else {
            this.textboxHoldReasonText.enabled = false;
            this.textboxHoldReasonText.text = "";
        }
        this.holdReasonChanged = true;
    }

    afterBillingExecution() {
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("hold_reason_code"))) {
            this.textboxHoldReasonText.enabled = true;
        } else {
            this.textboxHoldReasonText.enabled = false;
        }

        const orderId = this.sourceBilling.activeRow?.get("order_id");
        if (orderId) {
            this.layoutFreightItems.sourceBillingFreightGroup.search({ order_id: orderId }).then((fgi) => {
                const fgData = fgi.modelRows;
                this.layoutFreightItems.setFreightGroupItems(fgData);

                const fgpUid = fgData[0]?.data["fgp_uid"];
                if (fgpUid) {
                    this.sourceBrltlOrderHdrXFgp.search({ fgp_uid: fgpUid });
                }
            });
        }
    }

    viewOrder() {
        const orderId = this.sourceBilling.activeRow?.get("order_id");
        if (orderId != null)
            OrderTabView.navigateTo({ key: orderId, newTab: true })
    }


    /** This is an event handler for the onClick event of dataheaderOrders-sendBackToOperations. */
    sendBackToOperationsOnClick(event: ClickEvent) {
        try {
            const data = this.getRowData();
            const count = data.length;
            const dialogLayout = Layout.getLayout("lme/ar/InvoiceProcessingDeleteDialog") as InvoiceProcessingDeleteDialog;
            CommonDialogs.showYesNo(dialogLayout, "Send Back to Operations", {
                yesButtonCaption: "Confirm",
                noButtonCaption: "Cancel",
                xVisible: true
            }, false, 150).then(async clicked => {
                if (clicked) {
                    const message = count === 1
                        ? " record successfully sent back to operations."
                        : " records successfully sent back to operations.";
                    const onHold = dialogLayout.switchHold.checked;
                    const holdReason = dialogLayout.textboxHoldReason.text;
                    const result = await Api.post("lme/ar/invoice-delete", { on_hold: onHold, hold_reason: holdReason, billing_id_list: data });
                    if (result) {
                        const errors = result?.data[0]?.error_count;
                        setTimeout(() => Toast.showSuccessToast(count + message), 500);
                        if (errors > 0) {
                            const errorMessage = errors === 1
                                ? errors + " record was not sent back to operations with errors."
                                : errors + " records were not sent back to operations with errors.";
                            Snackbar.showWarningSnackbar(errorMessage);
                        } else {
                            this.close();
                        }
                    }
                }
            });

        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to send records back to operations");
        }
    }

    private getRowData() {
        const row = {
            billing_id: this.sourceBilling.data[0].get("id"),
            order_id: this.sourceBilling.data[0].get("order_id"),
            movement_id: this.sourceOrders.data[0].get("curr_movement_id")
        };
        const data = [row];
        return data;
    }

    async close() {
        const beforeCloseEvent = new CrudDecoratorCloseEvent(this.dataHeader, DataSourceMode.UPDATE, DataHeaderCloseAction.CANCEL, true);
        this.dataHeader.fireBeforeCloseListener(beforeCloseEvent);
        if (beforeCloseEvent.defaultPrevented === true) {
            log.debug(this, "Close of DataHeader cancelled: %o", this);
            return false;
        }
        if (this.dataHeader.navigateOnClose != null)
            await this.dataHeader.navigateOnClose(DataHeaderCloseAction.CANCEL);
        else {
            const destinationPath = this.dataHeader.afterClosePath ?? "";
            await Navigation.navigateTo(destinationPath);
        }
        const afterCloseEvent = new CrudDecoratorCloseEvent(this.dataHeader, DataSourceMode.UPDATE, DataHeaderCloseAction.CANCEL, false);
        this.dataHeader.fireAfterCloseListener(afterCloseEvent);
    }
    cancelZeroDollarBillOnClick(event: ClickEvent) {
        const message = "Cancellation of a zero-dollar invoice will delete the invoice record, create an invoice history record, and update the order.\n\n Are you sure you want to proceed?";
        const selected = this.getRowData();
        CommonDialogs.showYesNo(message, "Cancel Invoice(s)",
            { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                if (yes) {
                    await Api.post("lme/datafusion/cancel-invoice", { billing_id_list: selected }).then(result => {
                        //ERROR SNACKBAR
                        const errors = result.data[0].errors;
                        if (errors && errors.length > 0) {
                            const errorMessage = errors.map(error => `Invoice ID: ${error.error_id}, Description: ${error.error_description}`).join('; ');
                            Snackbar.showWarningSnackbar(errorMessage);
                        }
                        //WARNING SNACKBAR
                        const warnings = result.data[0].warnings;
                        if (warnings && warnings.length > 0) {
                            const warningMessage = warnings.map(warning => `Invoice ID: ${warning.warning_id}, Description: ${warning.warning_description}`).join('; ');
                            Snackbar.showWarningSnackbar(warningMessage);
                        }
                        const successMessage = result.data[0].successfully_cancelled === 0
                            ? result.data[0].successfully_cancelled + " invoice successfully cancelled."
                            : result.data[0].successfully_cancelled + " invoices successfully cancelled.";
                        this.handleApiResponse(result, successMessage);
                        this.close();
                    }).catch(error => {
                        console.error("Error cancelling invoices", error);
                    });
                }
            });
    }
    handleApiResponse(response: any, successMessage: string, errorMessage?: string) {
        if (response) {
            setTimeout(() => Toast.showSuccessToast(successMessage), 500);
        } else if (errorMessage) {
            Snackbar.showWarningSnackbar(errorMessage);
        }
    }

    checkboxReadyToProcessOnClick(event: ClickEvent) {
        if(this.checkboxReadyToProcess.checked) {
            this.activeRow.set("billing_user_id", LMEUserSettings.getUserId());
            this.activeRow.setValueAndLookupModelData("billing_user_id",
                LMEUserSettings.getUserId(),
                LMEUserSettings.getUserLookupModelData());
            this.textboxBillingUserId.text = LMEUserSettings.getUserName();
        } else {
            this.activeRow.set("billing_user_id", null);
            this.activeRow.removeLookupModelData("billing_user_id");
            this.textboxBillingUserId.text = "";
        }
    }
}
